import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = unauth, object = auth
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password, device_token: localStorage.getItem("vlog_device") || "" });
      if (data?.otp_required) return { ok: true, otpRequired: true, challengeId: data.challenge_id, emailHint: data.email_hint, emailed: data.emailed, method: data.method || "email" };
      finishLogin(data);
      return { ok: true, subscriptionLocked: !!data.subscription_locked };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const finishLogin = (data) => {
    if (data?.token) localStorage.setItem("vlog_token", data.token);
    if (data?.device_token) localStorage.setItem("vlog_device", data.device_token);
    setUser({ id: data.id, email: data.email, name: data.name, role: data.role, permissions: data.permissions || {}, mfa_enabled: !!data.mfa_enabled, mfa_enroll_required: !!data.mfa_enroll_required });
  };

  const verifyOtp = async (challengeId, code, rememberDevice) => {
    try {
      const { data } = await api.post("/auth/otp/verify", { challenge_id: challengeId, code, remember_device: rememberDevice });
      finishLogin(data);
      return { ok: true, subscriptionLocked: !!data.subscription_locked };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const resendOtp = async (challengeId) => {
    try {
      const { data } = await api.post("/auth/otp/resend", { challenge_id: challengeId });
      return { ok: true, challengeId: data.challenge_id, emailed: data.emailed, emailHint: data.email_hint, method: "email" };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const verifyRecovery = async (challengeId, recoveryCode, rememberDevice) => {
    try {
      const { data } = await api.post("/auth/mfa/recovery", { challenge_id: challengeId, recovery_code: recoveryCode, remember_device: rememberDevice });
      finishLogin(data);
      return { ok: true, subscriptionLocked: !!data.subscription_locked };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("vlog_token");
    sessionStorage.removeItem("kiosk_geo_verified");
    sessionStorage.removeItem("kiosk_checkin_draft");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, verifyOtp, resendOtp, verifyRecovery, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
